/**
 * HowTo structured data (JSON-LD) for step-by-step guides
 * Optimized for AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization)
 * Helps answer engines extract step-by-step instructions directly
 * Follows Schema.org HowTo specification
 */

import React from 'react';
import { JSONLDScript } from './jsonld-script';

/**
 * A single step in a HowTo guide
 */
export interface HowToStep {
  /** Step position (1-based) */
  position: number;
  /** Step name/title */
  name: string;
  /** Step description/instructions */
  text: string;
  /** Optional URL to step (if step has its own page) */
  url?: string;
  /** Optional image for the step */
  image?: string;
}

/**
 * Tool or item required for the HowTo
 */
export interface HowToTool {
  /** Tool name */
  name: string;
  /** Optional tool URL */
  url?: string;
}

interface HowToStructuredDataProps {
  /** HowTo name/title */
  name: string;
  /** HowTo description */
  description: string;
  /** Steps in the HowTo */
  steps: HowToStep[];
  /** Optional total time required (ISO 8601 duration, e.g., "PT5M" for 5 minutes) */
  totalTime?: string;
  /** Optional tools required */
  tools?: HowToTool[];
  /** Optional URL of the HowTo page */
  url?: string;
  /** Optional image for the HowTo */
  image?: string;
}

/**
 * HowTo structured data component
 *
 * Generates Schema.org HowTo JSON-LD for step-by-step guides.
 * This helps AI-powered search engines (ChatGPT, Perplexity, Google AI Overviews)
 * extract and present step-by-step instructions directly in their responses.
 *
 * @example
 * ```tsx
 * <HowToStructuredData
 *   name="How to Filter Quick Question Emails in Gmail"
 *   description="Step-by-step guide to create Gmail filters for quick question emails"
 *   steps={[
 *     {
 *       position: 1,
 *       name: "Go to Gmail Settings",
 *       text: "Navigate to Gmail Settings → Filters and Blocked Addresses"
 *     },
 *     {
 *       position: 2,
 *       name: "Create a new filter",
 *       text: "Click 'Create a new filter' button"
 *     }
 *   ]}
 *   totalTime="PT5M"
 *   tools={[{ name: "Gmail" }]}
 * />
 * ```
 *
 * @see https://schema.org/HowTo for complete schema specification
 */
export function HowToStructuredData({
  name,
  description,
  steps,
  totalTime,
  tools,
  url,
  image,
}: HowToStructuredDataProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'https://emailferret.io';
  const pageUrl = url || baseUrl;
  const imageUrl = image || `${baseUrl}/og-image.png`;

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    image: imageUrl,
    url: pageUrl,
    ...(totalTime && { totalTime }),
    ...(tools &&
      tools.length > 0 && {
        tool: tools.map((tool) => ({
          '@type': 'HowToTool',
          name: tool.name,
          ...(tool.url && { url: tool.url }),
        })),
      }),
    step: steps.map((step) => ({
      '@type': 'HowToStep',
      position: step.position,
      name: step.name,
      text: step.text,
      ...(step.url && { url: step.url }),
      ...(step.image && {
        image: {
          '@type': 'ImageObject',
          url: step.image,
        },
      }),
    })),
  };

  return <JSONLDScript data={howToSchema} id="howto-structured-data" />;
}
